export const AbilitySuperType = 'Ability';
export var AbilityType;
(function (AbilityType) {
    AbilityType[AbilityType["Activated"] = 0] = "Activated";
    AbilityType[AbilityType["Passive"] = 1] = "Passive";
    AbilityType[AbilityType["Spell"] = 2] = "Spell";
    AbilityType[AbilityType["Command"] = 3] = "Command";
    AbilityType[AbilityType["Prayer"] = 4] = "Prayer";
    AbilityType[AbilityType["BloodTithe"] = 5] = "BloodTithe";
    AbilityType[AbilityType["Fate"] = 6] = "Fate";
    AbilityType[AbilityType["Unknown"] = 99] = "Unknown";
})(AbilityType || (AbilityType = {}));
export function fromAoSData(data) {
    let cost = null;
    let type = AbilityType.Activated;
    if (data.keywords && data.keywords?.includes('Prayer')) {
        cost = data.cpCost;
        type = AbilityType.Prayer;
    }
    else if (data.keywords && data.keywords?.includes('Spell')) {
        cost = data.cpCost;
        type = AbilityType.Spell;
    }
    else if (data.name.includes('Passive')) {
        type = AbilityType.Passive;
        cost = null;
    }
    else if (Number(data.cpCost) > 0) {
        cost = data.cpCost;
        type = AbilityType.Command;
    }
    const ability = {
        id: data.name,
        type: type,
        superType: AbilitySuperType,
        name: data.name,
        timing: data.timing,
        keywords: data.keywords.map((k) => `**^^${k.toUpperCase().trim()}^^**`),
        effect: data.effect,
        color: convertAosPhaseToColor(data.phase),
        typeAttribute: convertAosIconToType(data.icon),
        declare: data.declare,
        usedBy: data.usedBy,
        // cpCost can arrive as a number from the source data, but IAbility.cost is a
        // string (and cost badges expect a string), so normalize it here.
        cost: cost == null ? null : String(cost),
        unlockCondition: '',
    };
    return ability;
}
// Inverse of fromAoSData: serialize a structured IAbility back into the raw AoS
// ability shape (IRuleAbility). Needed when an authored battleplan (which stores
// full IAbility objects) is turned into a RuleDocument for the rules browser,
// because the browser's renderer reconstructs abilities from the raw `ability`
// field via fromAoSData. fromAoSData re-derives id/superType/type and re-formats
// keywords, so this only needs to carry the source fields.
export function toAoSData(ability) {
    return {
        name: ability.name,
        timing: ability.timing,
        // fromAoSData re-applies the **^^...^^** wrapper, so emit plain keywords;
        // strip any existing formatting to avoid double-wrapping on round-trip.
        keywords: ability.keywords.map(stripKeywordFormatting),
        declare: ability.declare ?? '',
        effect: ability.effect,
        usedBy: ability.usedBy ?? '',
        cpCost: ability.cost ?? '',
        phase: convertColorToAosPhase(ability.color),
        icon: convertTypeToAosIcon(ability.typeAttribute),
    };
}
function stripKeywordFormatting(keyword) {
    return keyword
        .replace(/\*\*\^\^/g, '')
        .replace(/\^\^\*\*/g, '')
        .trim();
}
export function convertAosIconToType(icon) {
    // capitalize the first letter of the icon
    const typeName = icon.charAt(0).toUpperCase() + icon.slice(1);
    switch (typeName) {
        case 'BattleDamaged':
            return 'Damage';
        default:
            return typeName;
    }
}
export function convertAosPhaseToColor(phase) {
    switch (phase) {
        case 'startOfTurn':
            return 'Black';
        case 'heroPhase':
            return 'Yellow';
        case 'movementPhase':
            return 'Gray';
        case 'shootingPhase':
            return 'Blue';
        case 'chargePhase':
            return 'Orange';
        case 'combatPhase':
            return 'Red';
        case 'endOfTurn':
            return 'Purple';
        case 'defensive':
            return 'Green';
        default:
            return 'Black';
    }
}
// Inverse of convertAosPhaseToColor (used by toAoSData).
export function convertColorToAosPhase(color) {
    switch (color) {
        case 'Yellow':
            return 'heroPhase';
        case 'Gray':
            return 'movementPhase';
        case 'Blue':
            return 'shootingPhase';
        case 'Orange':
            return 'chargePhase';
        case 'Red':
            return 'combatPhase';
        case 'Purple':
            return 'endOfTurn';
        case 'Green':
            return 'defensive';
        case 'Black':
        default:
            return 'startOfTurn';
    }
}
// Inverse of convertAosIconToType (used by toAoSData). That function just
// capitalizes the icon's first letter (plus a BattleDamaged->Damage special
// case), so lowercasing the first letter inverts it.
export function convertTypeToAosIcon(type) {
    if (type === 'Damage')
        return 'battleDamaged';
    return type.charAt(0).toLowerCase() + type.slice(1);
}
